import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CircularProgress, Alert } from '@mui/material';
import StatsCard from './StatsCard';
import './AdminComponents.css';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? window.location.origin
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001');

// Fonction pour formater les bytes en format lisible
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// Fonction pour formater le temps en format lisible
const formatUptime = (seconds) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (days > 0) {
    return `${days}j ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

// Fonction pour formater la charge CPU
const formatLoadAvg = (loadAvg) => {
  return loadAvg.map(load => load.toFixed(2)).join(', ');
};

const SystemMetrics = () => {
  const { token } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/api/metrics`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des métriques système');
        }

        const data = await response.json();
        setMetrics(data);
      } catch (err) {
        console.error('Erreur lors du chargement des métriques système:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchMetrics();
    }
  }, [token]);

  if (loading) {
    return (
      <StatsCard title="Métriques système" loading={loading} error={null}>
        <div className="loading-container">
          <CircularProgress />
        </div>
      </StatsCard>
    );
  }

  if (error) {
    return (
      <StatsCard title="Métriques système" loading={false} error={error}>
        <Alert severity="error">{error}</Alert>
      </StatsCard>
    );
  }

  if (!metrics) {
    return null;
  }

  // Calcul du pourcentage d'utilisation mémoire système
  const systemMemoryUsagePercent = metrics.system?.memory
    ? Math.round(((metrics.system.memory.total - metrics.system.memory.free) / metrics.system.memory.total) * 100)
    : 0;

  // Calcul du pourcentage d'utilisation mémoire processus
  const processMemoryUsageMB = metrics.process?.memory?.heapUsed
    ? Math.round(metrics.process.memory.heapUsed / 1024 / 1024)
    : 0;

  return (
    <StatsCard title="Métriques système" loading={false} error={null}>
      <div className="stats-numbers">
        {/* Mémoire système */}
        {metrics.system?.memory && (
          <>
            <div className="stat-item">
              <span className="stat-label">Mémoire système utilisée</span>
              <span className="stat-value">{systemMemoryUsagePercent}%</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Mémoire totale</span>
              <span className="stat-value">{formatBytes(metrics.system.memory.total)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Mémoire libre</span>
              <span className="stat-value">{formatBytes(metrics.system.memory.free)}</span>
            </div>
          </>
        )}

        {/* CPU système */}
        {metrics.system?.cpu && (
          <>
            <div className="stat-item">
              <span className="stat-label">Cœurs CPU</span>
              <span className="stat-value">{metrics.system.cpu.cores}</span>
            </div>
            {metrics.system.cpu.load && (
              <div className="stat-item">
                <span className="stat-label">Charge CPU (1m, 5m, 15m)</span>
                <span className="stat-value" style={{ fontSize: '0.9rem' }}>
                  {formatLoadAvg(metrics.system.cpu.load)}
                </span>
              </div>
            )}
          </>
        )}

        {/* Uptime système */}
        {metrics.system?.uptime && (
          <div className="stat-item">
            <span className="stat-label">Uptime système</span>
            <span className="stat-value">{formatUptime(metrics.system.uptime)}</span>
          </div>
        )}

        {/* Mémoire processus */}
        {metrics.process?.memory && (
          <>
            <div className="stat-item">
              <span className="stat-label">Mémoire processus (heap)</span>
              <span className="stat-value">{processMemoryUsageMB} MB</span>
            </div>
            {metrics.process.memory.external && (
              <div className="stat-item">
                <span className="stat-label">Mémoire externe</span>
                <span className="stat-value">{formatBytes(metrics.process.memory.external)}</span>
              </div>
            )}
          </>
        )}

        {/* Métriques applicatives si disponibles */}
        {metrics.requests && (
          <div className="stat-item">
            <span className="stat-label">Requêtes totales</span>
            <span className="stat-value">{metrics.requests.total || 0}</span>
          </div>
        )}
        {metrics.errors && (
          <div className="stat-item">
            <span className="stat-label">Erreurs totales</span>
            <span className="stat-value">{metrics.errors.total || 0}</span>
          </div>
        )}
      </div>
    </StatsCard>
  );
};

export default SystemMetrics;
